import React from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Icon, IIcon } from '@umahealth/occipital/client'
import { cn } from '@/utils/cn'

/**
 * El menú agrupado que ve un perfil administrativo.
 *
 * Por qué no sale de `SidebarElement` de occipital: ese componente fija la
 * altura del item (`h-14`) y el padding del link (`py-4`) adentro, y occipital
 * es **compartido con otros clientes**. Achicarlo allá le cambiaría el menú a
 * todos. Así que acá se replica su markup —mismo `Icon`, mismas clases de
 * `isClosed`— en una versión más compacta, y occipital queda intacto.
 *
 * Un enfermero no pasa por acá: con una sola sección visible, el Header sigue
 * dibujando el menú de siempre.
 */

export type ElementoDeMenu = {
	href: string;
	text: string;
	icon: IIcon['name'];
	/**
	 * El link sale del portal. Se abre en otra pestaña y no pasa por el router
	 * de Next, que sólo sabe de rutas de esta app.
	 */
	externo?: boolean;
};

/**
 * Título de sección. Desaparece con el menú plegado: ahí sólo quedan los
 * iconos, y un título sin sus items debajo no se entiende.
 */
function TituloDeSeccion({ children }: { children: string }) {
	return (
		<li
			className="w-full px-4 pt-4 pb-1 group-[.isClosed]:hidden"
			aria-hidden
		>
			<span className="text-[11px] font-semibold uppercase tracking-wider text-grey-400">
				{children}
			</span>
		</li>
	)
}

function ItemDeMenu({ item }: { item: ElementoDeMenu }) {
	const pathname = usePathname()
	/**
	 * "/" sólo está activo en la raíz. Con `startsWith` quedaba encendido en
	 * todas las pantallas a la vez.
	 */
	const activo =
		item.href === '/'
			? pathname === '/'
			: pathname === item.href || pathname?.startsWith(`${item.href}/`)

	const clases = cn(
		'flex items-center gap-2 w-full py-2 px-3 rounded-xl transition-colors',
		'group-[.isClosed]:justify-center',
		activo ? 'bg-primary-25 text-primary' : 'hover:bg-primary-25 text-grey-500'
	)

	const contenido = (
		<>
			<Icon
				name={item.icon}
				size="size-5"
				color={activo ? 'text-primary' : 'text-grey-500'}
			/>
			<span className="group-[.isClosed]:sr-only text-sm truncate">
				{item.text}
			</span>
		</>
	)

	/**
	 * Un link que sale del portal va en un `<a>` común y en otra pestaña.
	 *
	 * Con `next/link` se rompía de dos maneras: el router trataba la URL como
	 * una ruta de esta app —y "Ingreso Portal Médicos" apuntaba a
	 * `/ingreso-portal-medicos`, que nunca existió, así que daba 404— y además
	 * se llevaba puesta la pantalla en la que estabas.
	 */
	if (item.externo) {
		return (
			<li className="w-full px-3 py-0.5">
				<a
					href={item.href}
					target="_blank"
					rel="noopener noreferrer"
					data-cy={`header-${item.text}-link`}
					className={clases}
				>
					{contenido}
				</a>
			</li>
		)
	}

	return (
		<li className="w-full px-3 py-0.5">
			<Link
				href={item.href}
				aria-current={activo ? 'page' : undefined}
				data-cy={`header-${item.text}-link`}
				className={clases}
			>
				{contenido}
			</Link>
		</li>
	)
}

export default function MenuPorSecciones({
	sueltos,
	secciones,
}: {
	/** Items sin sección, arriba de todo. Hoy es sólo Inicio. */
	sueltos: ElementoDeMenu[];
	/** En orden de display. Las secciones sin items ya vienen filtradas. */
	secciones: { titulo: string; items: ElementoDeMenu[] }[];
}) {
	return (
		<>
			{sueltos.map((item) => (
				<ItemDeMenu key={item.href} item={item} />
			))}
			{secciones.map((seccion) => (
				<React.Fragment key={seccion.titulo}>
					<TituloDeSeccion>{seccion.titulo}</TituloDeSeccion>
					{seccion.items.map((item) => (
						<ItemDeMenu key={item.href} item={item} />
					))}
				</React.Fragment>
			))}
		</>
	)
}
